import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    mat3,
    mat4,
    quat,
    vec2,
    vec3,
} from 'gl-matrix';
import { RotatedBoundingBox } from '~/engine/data/common/RotatedBoundingBox';
import { ModifierKeys } from '~/engine/tool/ModifierKeys';
import { ColorMask } from '~/engine/util/ColorMask';
import { Environment } from '../../data/Environment';
import { PointOfInterest } from '../../data/PointOfInterest';
import { BoundingBox } from '../../data/common/BoundingBox';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../tool/EventHandlerResult';
import { Rectangle3D } from '../../util/geometry/Rectangle3D';
import { PanZoom } from '../PanZoom';
import { TextureView } from '../TextureView';
import { View } from '../View';
import { ContourLayer } from '../layers/ContourLayer';
import { DoseLayer } from '../layers/DoseLayer';
import { ImageLayer } from '../layers/ImageLayer';
import { LayerClass } from '../layers/Layer';

export type MouseData = {
    patientCoordinates?: ReadonlyVec3; //patient space coordinates
    imageCoordinates?: Map<number, ReadonlyVec3>; //scan id, pixel coordinates
    dataValues?: Map<number, number>; //data source, value
    contours?: Map<number, number[]>; //structureSet ID, roi numbers of nearby structures
};

/**
 * Object type describing a dictionnary mapping {@link Orienatations} to a {@link mat3} transformation matrice.\
 */
export type OrientationDictionnary = {
    [key in Orientations]: mat3;
};

export enum Orientations {
    TRANSVERSAL_ORIENTATION = 'TRANSVERSAL_ORIENTATION',
    SAGITTAL_ORIENTATION = 'SAGITTAL_ORIENTATION',
    CORONAL_ORIENTATION = 'CORONAL_ORIENTATION',
}

/**
 * {@link PlaneView} is a {@link View} that visualizes 3D data from the persepective of a plane.
 */
export class PlaneView extends TextureView {
    public get planeIntersectionPoint(): PointOfInterest {
        return this.environment.planeIntersectionPoint;
    }

    public get panZoom(): PanZoom {
        return this.environment.panZoom;
    }

    private listener?: PlaneViewMouseListener;

    /**
     * Method to get the default background_color used by
     */
    public static get PLANEVIEW_DEFAULT_BACKGROUND_COLOR(): ColorMask {
        return new ColorMask(0.1, 0.1, 0.1);
    }

    /**
     * Orientation of the default transversal (axial) view.
     * Transformation from ViewSpace to PatientSpace
     * Viewspace X is along Worldspace X,
     * Viewspace Y is inverted along Worldspace Y,
     * Viewspace Z is inverted along Worldspace Z.
     */
    public static get TRANSVERSAL_ORIENTATION(): mat3 {
        return mat3.fromValues(1.0, 0.0, 0.0, 0.0, -1.0, 0.0, 0.0, 0.0, -1.0);
    }

    public static get TRANSVERSAL_ORIENTATION_INVERTED(): mat3 {
        return mat3.fromValues(-1.0, 0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, -1.0);
    }

    /**
     * Orientation of the default coronal (frontal) view.
     * Transformation from ViewSpace to PatientSpace
     * Viewspace X is along Worldspace X,
     * Viewspace Y is along Worldspace Z,
     * Viewspace Z is inverted along Worldspace Y.
     */
    public static get CORONAL_ORIENTATION(): mat3 {
        return mat3.fromValues(1.0, 0.0, 0.0, 0.0, 0.0, 1.0, 0.0, -1.0, 0.0);
    }

    public static get CORONAL_ORIENTATION_INVERTED(): mat3 {
        return mat3.fromValues(-1.0, 0.0, 0.0, 0.0, 0.0, 1.0, 0.0, 1.0, 0.0);
    }

    /**
     * Orientation of the default sagittal view.
     * Transformation from ViewSpace to PatientSpace
     * Viewspace X is along Worldspace Y,
     * Viewspace Y is along Worldspace Z,
     * Viewspace Z is inverted along Worldspace X.
     */
    public static get SAGITTAL_ORIENTATION(): mat3 {
        return mat3.fromValues(0.0, -1.0, 0.0, 0.0, 0.0, 1.0, -1.0, 0.0, 0.0);
    }

    public static get SAGITTAL_ORIENTATION_INVERTED(): mat3 {
        return mat3.fromValues(0.0, 1.0, 0.0, 0.0, 0.0, 1.0, 1.0, 0.0, 0.0);
    }

    /**
     * An {@link OrientationDictionary} object mapping {@link Orientations} enum keys to their corresponding {@link mat3} transformation matrices.
     */
    public static get ORIENTATIONS(): OrientationDictionnary {
        return {
            [Orientations.TRANSVERSAL_ORIENTATION]:
                PlaneView.TRANSVERSAL_ORIENTATION,
            [Orientations.SAGITTAL_ORIENTATION]: PlaneView.SAGITTAL_ORIENTATION,
            [Orientations.CORONAL_ORIENTATION]: PlaneView.CORONAL_ORIENTATION,
        };
    }

    public static get INVERTED_ORIENTATIONS(): OrientationDictionnary {
        return {
            [Orientations.TRANSVERSAL_ORIENTATION]:
                PlaneView.TRANSVERSAL_ORIENTATION_INVERTED,
            [Orientations.SAGITTAL_ORIENTATION]:
                PlaneView.SAGITTAL_ORIENTATION_INVERTED,
            [Orientations.CORONAL_ORIENTATION]:
                PlaneView.CORONAL_ORIENTATION_INVERTED,
        };
    }
    /**
     * The orientation of this {@link PlaneView} relative to PatientSpace.\
     * This is the {@link rotation} from ViewSpace to PatientSpace when {@link additionalRotation} is either undefined or identity.\
     */
    private readonly orientation: mat3;

    public setOrientation(
        newOrientationMatrix: ReadonlyMat3,
        newOrientationLabel?: Orientations
    ): void {
        if (newOrientationLabel) {
            this.label = newOrientationLabel;
        }
        mat3.copy(this.orientation, newOrientationMatrix);
        this.updateViewMatrix();
        this.updateView();
        this.updatePlaneIntersectionPoint(
            this.environment.planeIntersectionPoint.coordinates
        );
    }

    public getOrientation(): mat3 {
        return this.orientation;
    }

    /**
     * Optional {@link mat3 RotationMatrix} that is applied on top of {@link orientation} to define the complete {@link rotation} of this {@link PlaneView}.\
     */
    private readonly additionalRotation?: mat3;

    private _projectionViewMatrix: mat4;

    /**
     * Transformation from PatientSpace to ViewSpace.\
     * {@link _viewMatrix viewMatrix} is the inverse of {@link _transformationMatrix transformationMatrix}.
     */
    private _viewMatrix: mat4;

    /**
     * Transformation from ViewSpace to PatientSpace.\
     * {@link _transformationMatrix transformationMatrix} is the inverse of {@link _viewMatrix viewMatrix}.
     */
    protected _transformationMatrix: mat4;

    /**
     * {@link _projectionMatrix ProjectionMatrix} is a {@link mat4 matrix} that gives points orthographic projection.\
     * It has the value of:\
     * {1, 0, 0, 0,\
     * 0, 1, 0, 0,\
     * 0, 0, -2, 0,\
     * 0, 0, 0, 1}
     */
    private _projectionMatrix: mat4;

    /**
     * PatientSpace coordinates of the {@link PlaneView}'s center point. Defines the focal point of the {@link PlaneView}.\
     */
    protected _position: vec3;

    /**
     * sizeMm from middle to any of the edges.
     * So basically half of the size from left to right or top to bottom.
     * This is the format used in matrices
     */
    protected _scale: vec2 = vec2.fromValues(1, 1);

    /**
     * Constructor to create a new {@link PlaneView}.\
     * {@link PlaneView}s always require orientation, which defines the rotation of the PlaneView in PatientSpace.\
     * {@link PlaneView}s may also be provided a {@link HTMLCanvasElement} during the construction, that the {@link PlaneView} will use as it's output.
     * @param orientation The {@link PlaneView.orientation} of this {@link PlaneView} in PatientSpace.\
     * The {@link rotation} of the {@link PlaneView} may also be altered by modifying its {@link additionalRotation}.
     * @param canvas Optional canvas that this {@link PlaneView} will use as its own.
     */
    constructor(
        env: Environment,
        private readonly _eventTarget: EventTarget,
        orientation: mat3,
        canvas: HTMLCanvasElement,
        canvas2D: HTMLCanvasElement,
        public label?: Orientations
    ) {
        super(env, canvas, canvas2D);
        this.orientation = orientation;
        this._projectionMatrix = mat4.fromValues(
            1,
            0,
            0,
            0,
            0,
            1,
            0,
            0,
            0,
            0,
            -2,
            0,
            0,
            0,
            0,
            1
        );
        this._position = vec3.create();
        this._transformationMatrix = mat4.create();
        this._viewMatrix = mat4.create();
        this._projectionViewMatrix = mat4.create();
        this.updateProjectionViewMatrix();

        this.environment.panZoom.subscribe((value: number) => {
            if (value !== this._currentScale) {
                this.updateZoomScale(value);
            }
        });

        this.environment.planeIntersectionPoint.subscribe((point) => {
            if (this.updateToOrthoPosition(point)) {
                this.updateView();
            }
        });

        // TODO: still not a good place
        env.eventTarget.addEventListener('windowLevel_mousemove', (e) => {
            if (e instanceof CustomEvent && env.image) {
                const delta = e.detail as vec2;
                if (delta && env.image.windowLevel) {
                    env.updateImageObjectsWindowingLevel(
                        env.image.windowLevel.add(delta)
                    );
                }
            }
        });
    }

    /**
     * Method to get all the {@link TextureView.defaultLayers defaultLayers} of a {@link PlaneView}.
     * PlaneView's default Layers include {@link ImageLayer} and {@link ContourLayer}.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public get defaultLayers(): LayerClass<any>[] {
        return [ImageLayer, DoseLayer, ContourLayer];
    }

    /**
     * {@link ReadonlyMat3 Rotation} from ViewSpace to PatientSpace.\
     * Equal to {@link orientation} if {@link additionalRotation} is undefined or identity.\
     * Otherwise {@link orientation} * {@link additionalRotation}.
     */
    public get rotation(): ReadonlyMat3 {
        return this.additionalRotation
            ? mat3.multiply(
                  mat3.create(),
                  this.additionalRotation,
                  this.orientation
              )
            : this.orientation;
    }

    /**
     * {@link ReadonlyMat3 Rotation} from PatientSpace to ViewSpace.\
     * This is the {@link mat3.transpose Transpose} of {@link rotation}.
     */
    public get invRotation(): ReadonlyMat3 {
        return mat3.transpose(mat3.create(), this.rotation);
    }

    /**
     * Method to get the {@link _projectionViewMatrix projectionViewMatrix} of this {@link PlaneView}.\
     * This can be used to convert points directly from PatientSpace to ProjectionSpace.\
     * This is equal to {@link projectionMatrix} * {@link viewMatrix}.
     */
    public get projectionViewMatrix(): ReadonlyMat4 {
        return this._projectionViewMatrix;
    }

    /**
     * Method to get the {@link _viewMatrix viewMatrix} of this {@link PlaneView}.\
     * This can be used to convert points from PatientSpace to ViewSpace.\
     * This is the inverse of {@link transformationMatrix}.
     */
    public get viewMatrix(): ReadonlyMat4 {
        return this._viewMatrix;
    }

    /**
     * Method to get the {@link _projectionMatrix projectionMatrix} of this {@link PlaneView}.\
     * {@link PlaneView PlaneViews} use orthographic projection and so this matrix is constant.
     *
     * This can be used to convert points from ViewSpace to ProjectionSpace.\
     * However that kind of transformation is rarely needed,
     * it's usually more convenient to just transform directly from PatientSpace to ProjectionSpace.
     */
    public get projectionMatrix(): ReadonlyMat4 {
        return this._projectionMatrix;
    }

    /**
     * Method to get the {@link _transformationMatrix transformationMatrix} of this {@link PlaneView}.\
     * This can be used to convert points from ViewSpace to PatientSpace.\
     * This is the inverse of {@link viewMatrix}.
     */
    public get transformationMatrix(): ReadonlyMat4 {
        return this._transformationMatrix;
    }

    /**
     * Method to get {@link Rectangle3D} presentation of this {@link PlaneView}'s viewport.\
     * Technically this is the same as the {@link transformationMatrix},
     * but {@link Rectangle3D} offers some convenience methods, so providing that kind of object may be useful.
     */
    public get viewportInPatientSpace(): Rectangle3D {
        // TODO REMOVE THIS
        return new Rectangle3D(this._position, this.rotation, this._scale);
    }

    /**
     * Method to get the view area as a bounding box in patient coordinate system.
     */
    public getPatientSpaceBoundingBox(): BoundingBox {
        const halfView = vec3.fromValues(
            this._scale[0],
            -1 * this._scale[1],
            -1
        );
        const halfViewInPatientSpace = vec3.transformMat3(
            vec3.create(),
            halfView,
            this.rotation
        );
        const minInPatientSpace = vec3.subtract(
            vec3.create(),
            this._position,
            halfViewInPatientSpace
        );
        const maxInPatientSpace = vec3.add(
            vec3.create(),
            this._position,
            halfViewInPatientSpace
        );
        return new BoundingBox(
            vec3.min(vec3.create(), minInPatientSpace, maxInPatientSpace),
            vec3.max(vec3.create(), minInPatientSpace, maxInPatientSpace)
        );
    }

    /**
     * Method to get the resolution of this {@link PlaneView} in px/mm.
     */
    public get resolution(): ReadonlyVec2 {
        const resolution = vec2.divide(vec2.create(), this._scale, this.size);
        vec2.scale(resolution, resolution, 2);
        return resolution;
    }

    /**
     * Method to update the {@link position} of this {@link PlaneView}.
     */
    public setViewPosition(newPositionPatientSpace: ReadonlyVec3) {
        this._position = vec3.clone(newPositionPatientSpace);
        this.updateViewMatrix();
    }

    /**
     * Method to move this view to a new position in patient coordinates.
     */
    public panTo(newPositionPatientSpace: ReadonlyVec3) {
        const delta = vec3.subtract(
            vec3.create(),
            this._position,
            newPositionPatientSpace
        );
        vec3.copy(this._position, newPositionPatientSpace);
        this.updateViewMatrix();
        this.updateView();
        const newPlaneIntersectionPoint = vec3.subtract(
            vec3.create(),
            this.environment.planeIntersectionPoint.coordinates,
            delta
        );
        this.updatePlaneIntersectionPoint(newPlaneIntersectionPoint);
    }

    /**
     * Get the current center point position of the view in patient coordinates
     */
    public get position(): ReadonlyVec3 {
        return this._position;
    }

    /**
     * Method to move this view to align with the plane intersection point.
     */
    public updateToOrthoPosition(newOrthoPosition: ReadonlyVec3): boolean {
        const viewCenter = vec3.transformMat3(
            vec3.create(),
            this._position,
            this.invRotation
        );
        const orthoPointInView = vec3.transformMat3(
            vec3.create(),
            newOrthoPosition,
            this.invRotation
        );
        if (viewCenter[2] !== orthoPointInView[2]) {
            viewCenter[2] = orthoPointInView[2];
            //X, Y and Z are within image space, transform them into patient space
            vec3.transformMat3(this._position, viewCenter, this.rotation);
            this.updateViewMatrix();
            return true;
        }
        return false;
    }

    /**
     * Method to fit the view around a bounding box.\
     */
    public fitToView(targetBb: RotatedBoundingBox): number {
        // don't zoom too deep in case the structure is very small
        const minViewWidthMm = 60.0;
        const alignedBb = new BoundingBox();
        alignedBb.expandWithPoints(targetBb.getVertices(this.invRotation));
        const sizeMm = alignedBb.size;
        const scale = vec2.scale(
            vec2.create(),
            vec2.fromValues(
                Math.max(sizeMm[0], minViewWidthMm),
                Math.max(sizeMm[1], minViewWidthMm)
            ),
            0.5
        );
        const resolutions = vec2.divide(vec2.create(), this.size, scale);
        const resolution = Math.min(resolutions[0], resolutions[1]);

        const prevScale = vec2.clone(this._scale);

        vec2.scale(this._scale, this.size, 1 / resolution);

        // Bounding box center is NOT in patient coordinates, but in its own coordinate system
        const centerInPatientCoords = vec3.transformMat3(
            vec3.create(),
            targetBb.center,
            targetBb.rotation
        );

        this.setViewPosition(centerInPatientCoords);

        this.updateViewMatrix();

        return this._currentScale / (this._scale[0] / prevScale[0]);
    }

    private _currentScale: number = 1;

    public get currentScale(): number {
        return this._currentScale;
    }

    public resetCurrentScale(scale: number) {
        this._currentScale = scale;
    }

    public updateZoomScale(newScale: number) {
        vec2.scale(this._scale, this._scale, this._currentScale / newScale);
        this._currentScale = newScale;
        this.updateViewMatrix();
    }

    /**
     * Method to zoom to specific coordinates on the view.
     */
    public zoomTo(viewCoords: ReadonlyVec2, newScale: number) {
        const patientCoords = this.patientCoordsFromCanvasCoords(
            viewCoords[0],
            viewCoords[1]
        );

        this.updateZoomScale(newScale);
        const newPatientCoords = this.patientCoordsFromCanvasCoords(
            viewCoords[0],
            viewCoords[1]
        );

        const delta = vec3.subtract(
            vec3.create(),
            patientCoords,
            newPatientCoords
        );
        vec3.add(this._position, this._position, delta);
        this.updateViewMatrix();
        this.updateView();

        this.updatePlaneIntersectionPoint(patientCoords);
    }

    /**
     * Method to get the plane intersection coordinate value along the view's viewing direction.
     */
    public get planeCoordinates() {
        return vec3.dot(
            this.environment.planeIntersectionPoint.coordinates,
            this.viewDirection
        );
    }

    /**
     * Method to update the {@link _viewMatrix viewMatrix} and {@link _transformationMatrix transformationMatrix} of this {@link PlaneView}.\
     * {@link _transformationMatrix TransformationMatrix} is calculated using {@link mat4.fromRotationTranslationScale}, which takes\
     * {@link rotation}, {@link _position} and {@link scale} as parameters and produces a single {@link mat4 matrix}.
     *
     * What this does under the hood is it sets the {@link this._transformationMatrix transformationMatrix} to identity.\
     * Then translates it by {@link _position}\
     * Rotates by {@link rotation} and lastly\
     * Scales by {@link scale}.
     *
     * After the {@link _transformationMatrix transformationMatrix} has been updated, {@link _viewMatrix viewMatrix} is set to it's {@link mat4.invert inverse}.\
     * And lastly the {@link _projectionViewMatrix projectionViewMatrix} is {@link updateProjectionViewMatrix updated}.
     */
    protected updateViewMatrix(): void {
        mat4.fromRotationTranslationScale(
            this._transformationMatrix,
            quat.fromMat3(quat.create(), this.rotation),
            this._position,
            vec3.fromValues(this._scale[0], this._scale[1], 1)
        );

        mat4.invert(this._viewMatrix, this._transformationMatrix);
        this.updateProjectionViewMatrix();
    }

    /**
     * Method to update the size of this {@link PlaneView}.\
     * @param size The new size of this {@link PlaneView} in pixels.
     * @returns Reference to this {@link PlaneView}.
     */
    public resize(size: vec2): PlaneView {
        const currentAspectRatio = this._scale[0] / this._scale[1];
        const newAspectRatio = size[0] / size[1];
        this._scale[0] *= newAspectRatio / currentAspectRatio;
        this.updateViewMatrix();
        super.resize(size);
        return this;
    }

    /**
     * Private method to update the {@link _projectionViewMatrix projectionViewMatrix} of this {@link PlaneView}.\
     * Calculated as {@link _projectionMatrix projectionMatrix} * {@link _viewMatrix viewMatrix}.
     */
    private updateProjectionViewMatrix(): void {
        mat4.multiply(
            this._projectionViewMatrix,
            this._projectionMatrix,
            this._viewMatrix
        );
    }

    /**
     * Method to get the view direction vector of this PlaneView in PatientSpace.\
     * This is an unit vector in the PlaneView's normal direction. So along the View's z-axis.\
     * Calculated as vec3(0, 0, 1) * {@link rotation}.
     */
    public get viewDirection(): ReadonlyVec3 {
        return vec3.transformMat3(
            vec3.create(),
            vec3.fromValues(0, 0, 1),
            this.rotation
        );
    }

    /**
     * Method to get the view X direction vector of this PlaneView in PatientSpace.\
     */
    public get viewXDirection(): ReadonlyVec3 {
        return vec3.transformMat3(
            vec3.create(),
            vec3.fromValues(1, 0, 0),
            this.rotation
        );
    }

    /**
     * Method to get the view Y direction vector of this PlaneView in PatientSpace.\
     */
    public get viewYDirection(): ReadonlyVec3 {
        return vec3.transformMat3(
            vec3.create(),
            vec3.fromValues(0, 1, 0),
            this.rotation
        );
    }

    /**
     * Method to get the canvas coordinates {@link ReadonlyVec2 vector} of a Patient space point.\
     * @param point
     * @returns
     */
    public canvasCoordsFromPatientCoords(point: ReadonlyVec3): ReadonlyVec2 {
        const pointInView = vec3.transformMat4(
            vec3.create(),
            point,
            this.projectionViewMatrix
        );
        const point2d = vec2.fromValues(pointInView[0], pointInView[1]);
        point2d[0] = ((point2d[0] + 1) / 2) * this.size[0];
        point2d[1] = (1 - (point2d[1] + 1) / 2) * this.size[1];
        return point2d;
    }

    /**
     * Method to get the PatientSpace coordinates {@link ReadonlyVec3 vector} of a point on the canvas.\
     * @param canvasX
     * @param canvasY
     * @returns
     */
    public patientCoordsFromCanvasCoords(
        canvasX: number,
        canvasY: number
    ): vec3 {
        const coordinates = vec2.fromValues(
            canvasX / this.size[0],
            1 - canvasY / this.size[1]
        );
        coordinates[0] = coordinates[0] * 2 - 1;
        coordinates[1] = coordinates[1] * 2 - 1;
        const inv = mat4.invert(mat4.create(), this.projectionViewMatrix);
        const result = vec3.transformMat4(
            vec3.create(),
            vec3.fromValues(coordinates[0], coordinates[1], 0),
            inv
        );
        return result;
    }

    /**
     * Method to update the {@link PlaneView.planeIntersectionPoint planeIntersectionPoint} of this {@link PlaneView}.\
     *  @param point The new {@link PlaneView.planeIntersectionPoint planeIntersectionPoint} of this {@link PlaneView}.\
     */
    public updatePlaneIntersectionPoint(point: ReadonlyVec3) {
        this.environment.planeIntersectionPoint.coordinates = point;
        this._eventTarget.dispatchEvent(
            new Event('planeIntersectionPointChanged')
        );
    }

    /**
     * Function to snap the view to the closest image pixel.\
     */
    public snapToImagePixel() {
        const image = this.environment.image;
        if (image) {
            const curIntersectionPoint =
                this.environment.planeIntersectionPoint.coordinates;
            // Get the position of the plane intersection point in image coordinates
            const viewPosImage = image.grid.toVoxelSpace(curIntersectionPoint);

            // calculate the view direction and position along viewing direction in image space
            const viewDirUnit = this.viewDirection as vec3;
            const viewPosZdir = vec3.dot(viewDirUnit, viewPosImage);

            // the resolution of the image in the viewing direction
            const imageResViewDir = Math.abs(
                vec3.dot(viewDirUnit, image.grid.getRes())
            );

            // the distance to the image pixel edge
            const distanceToPixel = viewPosZdir % 1;

            // the distance to the image pixel edge in mm
            let distanceToPixelMm = distanceToPixel * imageResViewDir;

            if (Math.abs(distanceToPixelMm) > 1e-4) {
                // snap to the closer pixel edge
                if (Math.abs(distanceToPixel) > 0.5) {
                    distanceToPixelMm = (1 + distanceToPixel) * imageResViewDir;
                }

                // move the plane intersection point along the viewing direction
                this.updatePlaneIntersectionPoint(
                    vec3.add(
                        vec3.create(),
                        curIntersectionPoint,
                        vec3.scale(
                            vec3.create(),
                            viewDirUnit,
                            -1 * distanceToPixelMm
                        )
                    )
                );
            }
        }
    }

    /**
     * Method to get the canvas coordinates {@link ReadonlyVec2 vector} of a point in view space.
     **/
    public scrollLevelToPlaneIntersectionPoint(
        scrollType: string,
        delta: number
    ): void {
        const canvasHeight = this.canvas?.getBoundingClientRect().height;
        const canvasWidth = this.canvas?.getBoundingClientRect().width;
        if (!canvasHeight || !canvasWidth) {
            return;
        }
        const currentScrollLevel = this.planeIntersectionPointToScrollLevel();
        const newScrollLevel = {
            ...currentScrollLevel,
            [scrollType]: Math.min(
                Math.max(
                    scrollType === 'horizontal'
                        ? currentScrollLevel.horizontal +
                              (delta / canvasHeight) * 100
                        : currentScrollLevel.vertical +
                              (delta / canvasWidth) * 100,
                    0
                ),
                100
            ),
        };
        const offsetX = canvasWidth * (newScrollLevel.vertical / 100);
        const offsetY = canvasHeight * (newScrollLevel.horizontal / 100);
        const canvasCoords = vec2.fromValues(offsetX, offsetY);
        const newOrthoPoint = this.patientCoordsFromCanvasCoords(
            canvasCoords[0],
            canvasCoords[1]
        );
        this.updatePlaneIntersectionPoint(newOrthoPoint);
        this.updateView();
    }

    /**
     * Method to get the scroll level of the plane intersection point.\
     * @returns The scroll level of the plane intersection point as a percentage of the canvas size.\
     *
     **/
    public planeIntersectionPointToScrollLevel(): {
        vertical: number;
        horizontal: number;
    } {
        const orthoPoint = this.planeIntersectionPoint.coordinates;
        const canvasCoords = this.canvasCoordsFromPatientCoords(orthoPoint);
        const clientHeight = this.canvas?.getBoundingClientRect().height;
        const clientWidth = this.canvas?.getBoundingClientRect().width;
        if (clientHeight && clientWidth) {
            const vertical = (canvasCoords[0] / clientWidth) * 100;
            const horizontal = (canvasCoords[1] / clientHeight) * 100;
            return { vertical, horizontal };
        }
        return { vertical: 0, horizontal: 0 };
    }

    /* Method to convert {@link ReadonlyVec2 clientCoordinates} in between [0, pixelSize] to {@link ReadonlyVec3 PatientCoordinates}.\
     * This is done by first converting the {@link ReadonlyVec2 clientCoordinates} to {@link ReadonlyVec2 viewCoordinates} using {@link clientToViewCoordinates}.\
     * Then the {@link ReadonlyVec2 viewCoordinates} are converted to {@link ReadonlyVec3 PatientCoordinates} by {@link vec3.transformMat4 transforming} with {@link mat4.invert inverse} of {@link projectionViewMatrix}.\
     * The Z coordinate used for the conversion is set to 0, so the {@link ReadonlyVec3 PatientCoordinates} are on the view plane.
     * @param clientCoordinates {@link ReadonlyVec2 ClientCoordinates} in between [0, pixelSize].
     * @returns The {@link ReadonlyVec3 PatientCoordinates} at the coordinates specified by {@link clientCoordinates}.
     */
    public clientToPatientCoordinates(clientCoordinates: ReadonlyVec2): vec3 {
        const view = this.clientToViewCoordinates(clientCoordinates);
        const inv = mat4.invert(mat4.create(), this.projectionViewMatrix);
        const result = vec3.transformMat4(
            vec3.create(),
            vec3.fromValues(view[0], view[1], 0),
            inv
        );
        return result;
    }

    public setListener(listener: PlaneViewMouseListener): PlaneView {
        this.listener = listener;
        return this;
    }

    /**
     * MouseWheel event that was caught by the canvas and propagated by {@link ToolManager} to this {@link PlaneView}.
     * This will move the {@link PlaneView.planeIntersectionPoint planeIntersectionPoint} in the {@link PlaneView}'s {@link viewDirection normal direction}.
     * TODO: this probably shouldn't be in the view itself. We also catch the wheel event in the Viewport now.
     * @param delta The direction of the MouseWheel event.\
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event. These are not used.
     * @param coordinates The mouse position in client pixel coordinates at the time of the event. These are not used.
     * @param view The {@link PlaneView} on which the event occurred. (Same as this.)
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED} to indicate that the mouseWheelEvent was successfully handled.
     */
    public onMouseWheel(
        delta: 1 | -1,
        _modifierKeys: ModifierKeys,
        _canvasCoordinates: vec2,
        _view: PlaneView
    ): EventHandlerResult {
        if (!this.env.image) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const step = this.env.image.calculateStep(
            this.planeIntersectionPoint.coordinates,
            delta < 0
                ? this.viewDirection
                : vec3.negate(vec3.create(), this.viewDirection)
        );
        this.planeIntersectionPoint.add(step);
        this._eventTarget.dispatchEvent(
            new Event('planeIntersectionPointChanged')
        );
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    // Handler for selecting a hovered over structure
    public selectRoiAtCoords(viewCoordinates: ReadonlyVec2) {
        if (this.env.structureSet) {
            const patientCoords =
                this.clientToPatientCoordinates(viewCoordinates);

            const hoveredStructure =
                this.env.structureSet.findRoi(patientCoords);
            if (hoveredStructure) {
                this.env.setSelectedStructure(hoveredStructure);
            }
        }
    }

    /**
     * Resets the cursor style to the default ('default') cursor.
     *
     */

    public resetCursor() {
        const canvas = this.get2DCanvas;
        if (canvas) {
            const parentElement = canvas.parentElement;
            if (parentElement) {
                parentElement.style.cursor = 'default';
            }
        } else {
            document.body.style.cursor = 'default';
        }
    }

    public onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view?: View | undefined
    ): EventHandlerResult {
        if (!this.env.image) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        let directionVector: ReadonlyVec3 | null = null;
        if (key === 'PageUp' || key === 'ArrowUp') {
            directionVector = vec3.negate(vec3.create(), this.viewDirection);
        } else if (key === 'PageDown' || key === 'ArrowDown') {
            directionVector = this.viewDirection;
        }

        if (directionVector) {
            const step = this.env.image.calculateStep(
                this.planeIntersectionPoint.coordinates,
                directionVector
            );
            this.planeIntersectionPoint.add(step);
            this._eventTarget.dispatchEvent(
                new Event('planeIntersectionPointChanged')
            );
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }

        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }
}

export interface PlaneViewMouseListener {
    onMouseMoved(planeView: PlaneView, mouseData: MouseData): void;
}
